import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Download,
  Home,
  RefreshCw,
  Brain,
  Info,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Heart,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRAIT_COLORS = {
  openness: "#8b5cf6",
  conscientiousness: "#10b981",
  extraversion: "#d97706",
  agreeableness: "#ec4899",
  neuroticism: "#64748b",
};

const TRAIT_ORDER = [
  "extraversion",
  "agreeableness", 
  "conscientiousness",
  "neuroticism",
  "openness",
];

const TRAIT_DETAILS = {
  extraversion: {
    name: "Extraversion",
    authenticityNote: "Whether you're an extravert or introvert, your energy style is a gift. Extraverts bring enthusiasm and connection; introverts bring depth and thoughtfulness. Honor how you naturally recharge.",
    description: "Extraversion is marked by pronounced engagement with the external world. Extraverts enjoy being with people, are full of energy, and often experience positive emotions. They tend to be enthusiastic, action-oriented individuals who are likely to say \"Yes!\" or \"Let's go!\" to opportunities for excitement.",
    facets: [
      { name: "Friendliness", description: "Friendly people genuinely like other people and openly demonstrate positive feelings toward others. They make friends quickly and it is easy for them to form close, intimate relationships." },
      { name: "Gregariousness", description: "Gregarious people find the company of others pleasantly stimulating and rewarding. They enjoy the excitement of crowds." },
      { name: "Assertiveness", description: "High scorers like to speak out, take charge, and direct the activities of others. They tend to be leaders in groups." },
      { name: "Activity Level", description: "Active individuals lead fast-paced, busy lives. They move about quickly, energetically, and vigorously, and they are involved in many activities." },
      { name: "Excitement-Seeking", description: "High scorers on this scale are easily bored without high levels of stimulation. They love bright lights and hustle and bustle." },
      { name: "Cheerfulness", description: "This scale measures positive mood and feelings, not negative emotions. Persons who score high typically experience a range of positive feelings, including happiness, enthusiasm, optimism, and joy." }
    ]
  },
  agreeableness: {
    name: "Agreeableness",
    authenticityNote: "Your level of agreeableness reflects how you balance others' needs with your own. High agreeableness shows deep empathy; lower agreeableness shows strong boundaries. Both are valuable — the key is being genuine.",
    description: "Agreeableness reflects individual differences in concern with cooperation and social harmony. Agreeable individuals value getting along with others. They are therefore considerate, friendly, generous, helpful, and willing to compromise their interests with others'.",
    facets: [
      { name: "Trust", description: "A person with high trust assumes that most people are fair, honest, and have good intentions." },
      { name: "Morality", description: "High scorers on this scale see no need for pretense or manipulation when dealing with others and are therefore candid, frank, and sincere." },
      { name: "Altruism", description: "Altruistic people find helping other people genuinely rewarding. Consequently, they are generally willing to assist those who are in need." },
      { name: "Cooperation", description: "Individuals who score high on this scale dislike confrontations. They are perfectly willing to compromise or to deny their own needs in order to get along with others." },
      { name: "Modesty", description: "High scorers on this scale do not like to claim that they are better than other people." },
      { name: "Sympathy", description: "People who score high on this scale are tenderhearted and compassionate. They feel the pain of others vicariously and are easily moved to pity." }
    ]
  },
  conscientiousness: {
    name: "Conscientiousness",
    authenticityNote: "Your conscientiousness reflects how you approach goals and responsibilities. Highly conscientious people thrive with structure; less conscientious people thrive with flexibility. Neither is better — just different paths to success.",
    description: "Conscientiousness concerns the way in which we control, regulate, and direct our impulses. The benefits of high conscientiousness are obvious. Conscientious individuals avoid trouble and achieve high levels of success through purposeful planning and persistence.",
    facets: [
      { name: "Self-Efficacy", description: "Self-Efficacy describes confidence in one's ability to accomplish things. High scorers believe they have the intelligence, drive, and self-control necessary for achieving success." },
      { name: "Orderliness", description: "Persons with high scores on orderliness are well-organized. They like to live according to routines and schedules. They keep lists and make plans." },
      { name: "Dutifulness", description: "This scale reflects the strength of a person's sense of duty and obligation. Those who score high on this scale have a strong sense of moral obligation." },
      { name: "Achievement-Striving", description: "Individuals who score high on this scale strive hard to achieve excellence. Their drive to be recognized as successful keeps them on track toward their lofty goals." },
      { name: "Self-Discipline", description: "Self-discipline—what many people call will-power—refers to the ability to persist at difficult or unpleasant tasks until they are completed." },
      { name: "Cautiousness", description: "Cautiousness describes the disposition to think through possibilities before acting. High scorers on this scale take their time when making decisions." }
    ]
  },
  neuroticism: {
    name: "Neuroticism",
    authenticityNote: "Your emotional patterns are part of who you are. Those who feel deeply often have great empathy and creativity. Those who are more stable often provide calm in chaos. Understanding your emotional style helps you care for yourself.",
    description: "Neuroticism refers to the tendency to experience negative feelings. Those who score high on Neuroticism may experience primarily one specific negative feeling such as anxiety, anger, or depression, but are likely to experience several of these emotions.",
    facets: [
      { name: "Anxiety", description: "The 'fight-or-flight' system of anxious individuals is too easily and too often engaged. People who are high in anxiety often feel like something dangerous is about to happen." },
      { name: "Anger", description: "Persons who score high in Anger feel enraged when things do not go their way. They are sensitive about being treated fairly." },
      { name: "Depression", description: "This scale measures the tendency to feel sad, dejected, and discouraged. High scorers lack energy and have difficulty initiating activities." },
      { name: "Self-Consciousness", description: "Self-conscious individuals are sensitive about what others think of them. Their concern about rejection causes them to feel shy and uncomfortable around others." },
      { name: "Immoderation", description: "Immoderate individuals feel strong cravings and urges that they have difficulty resisting." },
      { name: "Vulnerability", description: "High scorers on Vulnerability experience panic, confusion, and helplessness when under pressure or stress." }
    ]
  },
  openness: {
    name: "Openness to Experience",
    authenticityNote: "Your openness reflects how you engage with ideas and experiences. High openness brings creativity and curiosity; lower openness brings practicality and focus. Both approaches have tremendous value in different contexts.",
    description: "Openness to Experience describes a dimension of cognitive style that distinguishes imaginative, creative people from down-to-earth, conventional people. Open people are intellectually curious, appreciative of art, and sensitive to beauty.",
    facets: [
      { name: "Imagination", description: "To imaginative individuals, the real world is often too plain and ordinary. High scorers on this scale use fantasy as a way of creating a richer, more interesting world." },
      { name: "Artistic Interests", description: "High scorers on this scale love beauty, both in art and in nature. They become easily involved and absorbed in artistic and natural events." },
      { name: "Emotionality", description: "Persons high on Emotionality have good access to and awareness of their own feelings." },
      { name: "Adventurousness", description: "High scorers on adventurousness are eager to try new activities, travel to foreign lands, and experience different things." },
      { name: "Intellect", description: "Intellect and artistic interests are the two most important, central aspects of openness to experience. High scorers on Intellect love to play with ideas." },
      { name: "Openness to Values", description: "This facet reflects a readiness to re-examine social, political, and religious values. Open individuals are willing to challenge convention and embrace new ideas and perspectives." }
    ]
  }
};

export default function ResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTraits, setExpandedTraits] = useState({});
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchResult();
  }, [id]);

  const fetchResult = async () => {
    try {
      const response = await axios.get(`${API}/results/${id}`);
      setResult(response.data);
      const expanded = {};
      Object.keys(response.data.results).forEach((trait) => {
        expanded[trait] = true;
      });
      setExpandedTraits(expanded);
    } catch (error) {
      console.error("Failed to fetch result:", error);
      toast.error("Failed to load results");
    } finally {
      setLoading(false);
    }
  };

  const toggleTrait = (trait) => {
    setExpandedTraits((prev) => ({
      ...prev,
      [trait]: !prev[trait],
    }));
  };

  const getScoreLabel = (percentile) => {
    if (percentile <= 30) return "Low";
    if (percentile <= 70) return "Average";
    return "High";
  };

  const generatePDF = async () => {
    setIsGeneratingPdf(true);
    toast.info("Generating PDF report...");

    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: 900,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight) * 0.95;
      const imgX = (pdfWidth - imgWidth * ratio) / 2;

      const pageHeight = pdfHeight - 20;
      const contentHeight = imgHeight * ratio;
      let heightLeft = contentHeight;
      let position = 10;

      pdf.addImage(imgData, "PNG", imgX, position, imgWidth * ratio, imgHeight * ratio);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - contentHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", imgX, position, imgWidth * ratio, imgHeight * ratio);
        heightLeft -= pageHeight;
      }

      pdf.save(`Personality-Report-${result.user_name.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Results Not Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find the results you're looking for.</p>
          <Button onClick={() => navigate("/")} className="bg-sky-500 hover:bg-sky-600">Take New Assessment</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-4">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/wzCO7KFXOfMKBfoO2dpF/media/6774787e91c39998b2e142f0.jpeg" 
                alt="IntegrityGO" 
                className="h-10 md:h-12"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="border-gray-300"
                data-testid="home-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
        <div ref={reportRef} className="bg-white rounded-2xl shadow-sm max-w-4xl mx-auto">
          {/* Report Header */}
          <div className="p-8 border-b border-gray-100">
            <div className="flex items-center justify-center gap-4 mb-6">
              <img 
                src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/wzCO7KFXOfMKBfoO2dpF/media/6774787e91c39998b2e142f0.jpeg" 
                alt="IntegrityGO" 
                className="h-12"
              />
            </div>
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Your Personality Profile
              </h2>
              <p className="text-gray-600 text-lg mb-1">Results for {result.user_name}</p>
              <p className="text-gray-500 text-sm">
                Completed on{" "}
                {new Date(result.completed_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>

            <div className="bg-sky-50 rounded-xl p-6 text-sm text-gray-700 space-y-3 border border-sky-100">
              <p className="font-semibold text-sky-800">Understanding Your Results</p>
              <p>
                This assessment reveals your unique personality traits — not to label you, but to help you 
                understand yourself better. There are no "good" or "bad" scores. Every trait has strengths, 
                and the goal is to embrace who you naturally are.
              </p>
              <p>
                Scores are <strong>percentiles</strong> based on normative data from 619,150 respondents.
                A percentile of 65 means you scored higher than 65% of people.{" "}
                <strong>Low</strong> (1-30th percentile), <strong>Average</strong> (31-70th), 
                <strong> High</strong> (71-99th). Every position on the spectrum has value.
              </p>
            </div>
          </div>

          {/* Trait Sections */}
          <div className="divide-y divide-gray-100">
            {TRAIT_ORDER.map((traitKey, index) => {
              const trait = result.results[traitKey];
              const traitDetail = TRAIT_DETAILS[traitKey];
              const isExpanded = expandedTraits[traitKey];
              const scoreLabel = getScoreLabel(trait.percentile);

              return (
                <motion.div
                  key={traitKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-8"
                  data-testid={`trait-section-${traitKey}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-4 h-16 rounded"
                        style={{ backgroundColor: TRAIT_COLORS[traitKey] }}
                      />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          {traitDetail.name}
                        </h3>
                        <p className="text-gray-500">
                          Your score: <span className="font-semibold">{scoreLabel}</span>
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-bold text-gray-900">
                        {trait.percentile}
                      </span>
                      <span className="text-sm text-gray-400 ml-1">%ile</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="ml-1 text-gray-400 cursor-help">
                              <Info className="w-4 h-4 inline" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Raw Score: {trait.score}/{trait.max_score}</p>
                            <p>Percentile: {trait.percentile}th — based on norms from 619,150 respondents</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>

                  {/* Score Bar */}
                  <div className="mb-6">
                    <div className="relative">
                      <div className="flex justify-between text-xs text-gray-400 mb-1 px-1">
                        {[1, 10, 20, 30, 40, 50, 60, 70, 80, 90, 99].map((num) => (
                          <span key={num}>{num}</span>
                        ))}
                      </div>
                      <div className="h-8 bg-gray-100 rounded relative overflow-hidden">
                        <motion.div
                          className="absolute h-full rounded"
                          style={{ backgroundColor: TRAIT_COLORS[traitKey] }}
                          initial={{ width: 0 }}
                          animate={{ width: `${trait.percentile}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                        />
                        <motion.div
                          className="absolute top-0 h-full w-1 bg-gray-900"
                          initial={{ left: 0 }}
                          animate={{ left: `${trait.percentile}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                        />
                      </div>
                      <div className="flex justify-between text-xs mt-1 px-1">
                        <span className="text-gray-500 font-medium">Low (1-30)</span>
                        <span className="text-gray-500 font-medium">Average (31-70)</span>
                        <span className="text-gray-500 font-medium">High (71-99)</span>
                      </div>
                    </div>
                  </div>

                  {/* Your Summary - Personalized based on score */}
                  <div 
                    className="rounded-xl p-6 mb-4 border-2"
                    style={{ 
                      backgroundColor: `${TRAIT_COLORS[traitKey]}08`,
                      borderColor: `${TRAIT_COLORS[traitKey]}30`
                    }}
                  >
                    <p 
                      className="font-semibold flex items-center gap-2 mb-3 text-lg"
                      style={{ color: TRAIT_COLORS[traitKey] }}
                    >
                      <Brain className="w-5 h-5" />
                      Your {traitDetail.name} Summary
                    </p>
                    <p className="text-gray-700 leading-relaxed text-base">
                      {trait.summary || trait.insight}
                    </p>
                  </div>

                  {/* Authenticity Note */}
                  <div className="bg-sky-50 rounded-xl p-5 mb-4 border border-sky-100">
                    <p className="text-sky-900 font-medium flex items-center gap-2 mb-2">
                      <Heart className="w-4 h-4" />
                      Embrace Your Authentic Self
                    </p>
                    <p className="text-sky-800">{traitDetail.authenticityNote}</p>
                  </div>

                  <button
                    onClick={() => toggleTrait(traitKey)}
                    className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium mb-4"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                    {isExpanded ? "Hide Details" : "Show Details"}
                  </button>

                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="space-y-6"
                    >
                      <div className="bg-gray-50 rounded-xl p-6">
                        <p className="text-gray-700 leading-relaxed mb-4">
                          {traitDetail.description}
                        </p>
                        <div
                          className="border-l-4 pl-4 py-2"
                          style={{ borderColor: TRAIT_COLORS[traitKey] }}
                        >
                          <p className="text-gray-800 font-medium">
                            {trait.insight}
                          </p>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-4">
                          Facets of {traitDetail.name}
                        </h4>
                        <div className="space-y-3">
                          {traitDetail.facets.map((facet, idx) => {
                            // Find matching facet score from API response
                            const facetScore = trait.facet_scores?.find(
                              (fs) => fs.name === facet.name
                            );
                            const scorePercentile = facetScore?.percentile ?? 50;
                            
                            return (
                              <div
                                key={facet.name}
                                className="bg-gray-50 rounded-lg p-4"
                              >
                                <div className="flex items-start gap-3">
                                  <span
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 mt-1"
                                    style={{ backgroundColor: TRAIT_COLORS[traitKey] }}
                                  >
                                    {idx + 1}
                                  </span>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-2">
                                      <h5 className="font-semibold text-gray-800">
                                        {facet.name}
                                      </h5>
                                      <span 
                                        className="text-sm font-bold px-2 py-1 rounded"
                                        style={{ 
                                          backgroundColor: `${TRAIT_COLORS[traitKey]}15`,
                                          color: TRAIT_COLORS[traitKey]
                                        }}
                                      >
                                        {scorePercentile}%ile
                                      </span>
                                    </div>
                                    {/* Facet score bar */}
                                    <div className="h-2 bg-gray-200 rounded-full mb-2 overflow-hidden">
                                      <motion.div
                                        className="h-full rounded-full"
                                        style={{ backgroundColor: TRAIT_COLORS[traitKey] }}
                                        initial={{ width: 0 }}
                                        animate={{ width: `${scorePercentile}%` }}
                                        transition={{ duration: 0.6, delay: idx * 0.1 }}
                                      />
                                    </div>
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                      {facet.description}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="p-8 bg-gray-900 rounded-b-2xl">
            <div className="text-center">
              <h3 className="text-xl font-bold text-white mb-3">
                You Are Already DOPE!
              </h3>
              <p className="text-gray-400 mb-6 max-w-xl mx-auto">
                Understanding your personality is the first step to showing up genuinely in every 
                relationship and conversation. The IntegrityGO philosophy is simple: be yourself, 
                because that's who people want to connect with.
              </p>
              <a
                href="https://salesengine.integritygo.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-sky-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-sky-600 transition-colors"
              >
                Learn More About IntegrityGO
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-col items-center gap-4">
          <div className="flex justify-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="px-8"
              data-testid="take-again-btn"
            >
              Take Another Assessment
            </Button>
          </div>

          <Dialog>
            <DialogTrigger asChild>
              <button
                className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-sky-600 transition-colors mt-2"
                data-testid="how-scoring-works-btn"
              >
                <HelpCircle className="w-4 h-4" />
                How Scoring Works
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-gray-900">
                  How Your Scores Are Calculated
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">The Assessment</h4>
                  <p>
                    This test uses 120 items from the <strong>International Personality Item Pool (IPIP)</strong>, 
                    measuring 5 broad personality domains and 30 specific facets (6 per domain). Each item 
                    is rated on a 1–5 scale from "Very Inaccurate" to "Very Accurate."
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Reverse-Scored Items</h4>
                  <p>
                    Some items are negatively keyed (e.g., "Prefer to be alone" for Extraversion). 
                    These are reverse-scored so that higher totals always mean more of the trait.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Normative Percentiles</h4>
                  <p>
                    Your raw score is compared against <strong>population norms</strong> derived from 
                    a sample of <strong>619,150 respondents</strong> (Johnson, 2014). We convert your 
                    score into a <strong>z-score</strong> using the population mean and standard deviation 
                    for each trait, then map it to a percentile via the standard normal distribution.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-3 mt-2 font-mono text-xs text-gray-600">
                    z = (your_score – population_mean) / standard_deviation<br />
                    percentile = normal_cdf(z) × 100
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">What Percentiles Mean</h4>
                  <p>
                    A percentile of <strong>65</strong> means you scored higher than 65% of people in 
                    the reference population. Scores are classified as:
                  </p>
                  <ul className="mt-1 space-y-1 pl-4">
                    <li><strong>Low</strong> (1st–30th percentile)</li>
                    <li><strong>Average</strong> (31st–70th percentile)</li>
                    <li><strong>High</strong> (71st–99th percentile)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Trait-Specific Norms</h4>
                  <p>
                    Each trait has its own population mean and standard deviation. For example, 
                    Agreeableness has a higher population mean (~89) than Neuroticism (~67) on 
                    the 24–120 raw scale, so the same raw score produces different percentiles 
                    depending on the trait.
                  </p>
                </div>
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-xs text-gray-400">
                    Based on the IPIP-NEO-120 (Johnson, 2014). Items sourced from{" "}
                    <a href="https://ipip.ori.org" target="_blank" rel="noopener noreferrer" className="underline hover:text-sky-500">
                      ipip.ori.org
                    </a>. 
                    This is a research instrument, not a clinical diagnostic tool.
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
